/*
 * Brand
 *
 * Resolves a dealership's own colours into the tokens the marketing surface
 * draws with. Every replicated website belongs to a different company, so
 * there is no fixed brand colour here the way there is in a single-brand
 * storefront. The accent arrives at runtime with the dealership preferences
 * and has to be made safe before anything is painted with it.
 *
 * Two problems come with author-supplied colour, and each has its own token:
 *
 *   1. A pale brand colour is unreadable as text on a white page. A dealership
 *      whose primary is a bright yellow would otherwise get yellow links.
 *      `ink()` steps the colour toward black until it clears WCAG AA, so text
 *      stays legible while still reading as the dealership's colour.
 *
 *   2. On a FILLED surface the text is always white, because near-black
 *      lettering on a brand-coloured button reads as unfinished. White cannot
 *      simply be forced on, on a mid-tone green it lands around 2:1, so
 *      `fill()` deepens the ground until white clears AA instead. The button
 *      stays recognisably the dealership's colour; it just gets darker.
 *
 * For the common case, a mid-to-dark brand colour, `ink()`, `primary()` and
 * `fill()` return the same value and none of this is visible.
 *
 * Note the two dealership colours do different jobs. `colors.primary` is the
 * accent: links, calls to action, labels. `colors.secondary` is a SURFACE.
 * The replicated site has always painted the header band with it, so it is
 * exposed separately as `surface()` and is never used as an accent.
 */

// Used until the dealership preferences resolve, and for any dealership whose
// record is missing or malformed. The blue is the storefront's own accent.
const FALLBACK = {
  primary: '#0071E3',
  secondary: '#1D1D1F'
};

// The marketing surface is mostly a white-ground design, but two sections,
// the stakes band and the walkthrough call to action, sit on near-black. A
// dealership whose brand is a deep navy would vanish against that ground, so
// the accent is resolved separately for each.
const GROUND = [255, 255, 255];
const DARK_GROUND = [29, 29, 31]; // Theme.colors.dark()
const DARK_INK = [29, 29, 31]; // #1D1D1F
const LIGHT_INK = [255, 255, 255];

// WCAG AA for body text, and the lower bar for a UI component's boundary.
const MIN_CONTRAST = 4.5;
const UI_CONTRAST = 3;
const parseHex = value => {
  let hex = String(value || '').trim().replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(char => `${char}${char}`).join('');
  }
  if (hex.length !== 6 || /[^0-9a-f]/i.test(hex) === true) {
    return null;
  }
  return [0, 2, 4].map(offset => parseInt(hex.slice(offset, offset + 2), 16));
};
const toHex = channels => {
  return `#${channels.map(channel => {
    let value = Math.round(Math.min(255, Math.max(0, channel)));
    return value.toString(16).padStart(2, '0');
  }).join('')}`.toUpperCase();
};

// WCAG 2.1 relative luminance.
const luminance = channels => {
  let values = channels.map(channel => {
    let value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * values[0] + 0.7152 * values[1] + 0.0722 * values[2];
};
const contrast = (a, b) => {
  let high = Math.max(luminance(a), luminance(b));
  let low = Math.min(luminance(a), luminance(b));
  return (high + 0.05) / (low + 0.05);
};
const mix = (channels, target, amount) => {
  return channels.map((channel, index) => channel + (target[index] - channel) * amount);
};

// Step the colour away from the ground until it clears AA against it, toward
// black on a light ground, toward white on a dark one. A colour that already
// clears it is returned untouched, which is the common case. The step count is
// bounded so a malformed colour cannot spin.
const readableOn = (channels, ground) => {
  let target = luminance(ground) > 0.5 ? [0, 0, 0] : [255, 255, 255];
  let result = channels;
  let steps = 0;
  while (contrast(result, ground) < MIN_CONTRAST && steps < 24) {
    result = mix(result, target, 0.06);
    steps++;
  }
  return result;
};

// Filled brand surfaces carry WHITE text, always. A brand colour is a mid-tone
// far more often than not, and on a mid-tone the contrast maths picks near
// black, which is legible but reads as unfinished next to every other button
// on the web.
//
// The honest way to get white text is not to force it onto a ground it cannot
// sit on. It is to deepen the ground until white genuinely works. A dealership
// whose green is #7AC17A gets a deeper green, not black lettering: still
// recognisably their colour, and legible at AA. A colour that already carries
// white is returned untouched, which covers most navies, reds and blues.
const readableFill = channels => {
  let result = channels;
  let steps = 0;
  while (contrast(result, LIGHT_INK) < MIN_CONTRAST && steps < 40) {
    result = mix(result, [0, 0, 0], 0.05);
    steps++;
  }
  return result;
};

// The same filled control, sitting on one of the near-black bands. Two things
// have to hold at once there: white must clear AA on the button, AND the button
// itself must be distinguishable from the ground behind it (3:1, the WCAG
// threshold for a UI component boundary). A deep navy satisfies the first
// easily and fails the second badly, it disappears into the band, so it is
// lightened until it separates, stopping before white stops working.
const readableFillOnDark = channels => {
  let result = readableFill(channels);
  let steps = 0;
  while (contrast(result, DARK_GROUND) < UI_CONTRAST && steps < 40) {
    let next = mix(result, [255, 255, 255], 0.04);
    // never trade the label's legibility for the button's edge
    if (contrast(next, LIGHT_INK) < MIN_CONTRAST) {
      break;
    }
    result = next;
    steps++;
  }
  return result;
};

// Hover moves away from the page ground in whichever direction is visible:
// a dark brand lightens, a light brand deepens.
const shift = channels => {
  return luminance(channels) > 0.5 ? mix(channels, [0, 0, 0], 0.12) : mix(channels, [255, 255, 255], 0.12);
};

// Whichever of near-black or white reads better on the supplied colour.
const foreground = channels => {
  return contrast(channels, DARK_INK) >= contrast(channels, LIGHT_INK) ? toHex(DARK_INK) : toHex(LIGHT_INK);
};
const resolve = (colors = {}) => {
  let primary = parseHex(colors.primary) || parseHex(FALLBACK.primary);
  let secondary = parseHex(colors.secondary) || parseHex(FALLBACK.secondary);

  // On the dark bands the accent is also used as a filled surface, so it has
  // to be light enough both to read as text and to take dark text on top.
  let onDark = readableOn(primary, DARK_GROUND);
  let fill = readableFill(primary);
  return {
    fill: toHex(fill),
    fillOnDark: toHex(readableFillOnDark(primary)),
    ink: toHex(readableOn(primary, GROUND)),
    inkOnDark: toHex(onDark),
    onInkOnDark: foreground(onDark),
    onFill: toHex(LIGHT_INK),
    onPrimary: foreground(primary),
    onSurface: foreground(secondary),
    primary: toHex(primary),
    primaryHover: toHex(shift(primary)),
    surface: toHex(secondary),
    surfaceHover: toHex(shift(secondary))
  };
};

// Resolved once the dealership preferences land. Held at module scope rather
// than in context so that Theme.js, which is a plain object of functions, not
// a component, can read it from any call site without threading props.
let brand = null;
const Brand = {
  set: colors => {
    brand = resolve(colors);

    // Hand the accent to the stylesheet as well. A few pieces of the
    // marketing surface are painted by CSS rather than inline styles (the
    // quote badge, text selection), and they have to match.
    let root = document.documentElement;
    // --brand is the FILL, not the authored colour, because the only CSS
    // that uses it (the quote badge) puts white text on top of it.
    root.style.setProperty('--brand', brand.fill);
    root.style.setProperty('--brand-ink', brand.ink);
    root.style.setProperty('--brand-on', brand.onFill);
    root.style.setProperty('--brand-raw', brand.primary);
    root.style.setProperty('--brand-surface', brand.surface);
    root.style.setProperty('--brand-surface-on', brand.onSurface);
    return brand;
  },
  // Falling back rather than returning null keeps every call site total, so
  // components render correctly before preflight completes.
  get: () => brand || resolve({}),
  // Accent for text, borders, and tints on the page ground. Contrast safe.
  ink: () => Brand.get().ink,
  // The same accent, resolved against the near-black bands instead.
  inkOnDark: () => Brand.get().inkOnDark,
  onInkOnDark: () => Brand.get().onInkOnDark,
  // Filled controls: the colour deepened until white text clears AA on it,
  // with the white that sits on top. See readableFill above.
  fill: () => Brand.get().fill,
  onFill: () => Brand.get().onFill,
  // The same, for filled controls sitting on the near-black bands.
  fillOnDark: () => Brand.get().fillOnDark,
  // The dealership's colour exactly as authored, for marks nothing sits on.
  primary: () => Brand.get().primary,
  primaryHover: () => Brand.get().primaryHover,
  onPrimary: () => Brand.get().onPrimary,
  // The dealership's secondary colour, only ever used as a ground.
  surface: () => Brand.get().surface,
  surfaceHover: () => Brand.get().surfaceHover,
  onSurface: () => Brand.get().onSurface,
  // Tint of the accent, for chips, wash backgrounds, and focus rings.
  tint: (alpha = 0.08, color) => {
    let channels = parseHex(color || Brand.get().ink) || [0, 0, 0];
    return `rgba(${channels.join(',')},${alpha})`;
  },
  // Exposed for callers that need the maths on an arbitrary colour, such as
  // choosing a foreground for a dealership logo backdrop.
  utils: {
    contrast: (a, b) => {
      let first = parseHex(a);
      let second = parseHex(b);
      return first && second ? contrast(first, second) : 0;
    },
    foreground: color => {
      let channels = parseHex(color);
      return channels ? foreground(channels) : toHex(DARK_INK);
    },
    isPale: color => {
      let channels = parseHex(color);
      return channels ? luminance(channels) > 0.75 : false;
    }
  }
};
export default Brand;